import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import {
  InputText,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import Button, {
  ButtonSize
} from '../../../../../shared/components/core-ui/button/button.component';
import { UserRole } from '../../../../../shared/enums/user-role.enum';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import { updateOneReservations } from '../../../../shared/services/data.service';
import { birdToast } from '../../../../../shared/components/core-ui/toast';

interface AmountCurrency {
  amount: string;
  currency: string;
}

const CURRENCY_OPTIONS = [
  { value: 'egp', label: 'EGP' },
  { value: 'usd', label: 'USD' }
];

const FINANCE_ROLES = [
  UserRole.FINANCE_MANAGER,
  UserRole.FINANCE_SPECIALIST,
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN
];

export interface FinancialAdjustmentsProps {
  data: {
    reservation: ReservationDetailsUiModel;
  };
  config: {
    isPermited: (roles: UserRole[]) => boolean;
  };
}

export const FinancialAdjustments: React.FC<FinancialAdjustmentsProps> = ({
  data: { reservation },
  config: { isPermited }
}) => {
  const canEdit = isPermited(FINANCE_ROLES);

  const [refundInput, setRefundInput] = useState<AmountCurrency>({ amount: '', currency: 'egp' });
  const [compensationInput, setCompensationInput] = useState<AmountCurrency>({
    amount: '',
    currency: 'egp'
  });
  const [insuranceInput, setInsuranceInput] = useState<AmountCurrency>({
    amount: '',
    currency: 'egp'
  });
  const [isSavingFinancial, setIsSavingFinancial] = useState(false);

  useEffect(() => {
    if (!reservation) return;
    setRefundInput({
      amount:
        reservation.refund?.amount !== undefined && reservation.refund?.amount !== null
          ? String(reservation.refund.amount)
          : '',
      currency: reservation.refund?.currency || 'egp'
    });
    setCompensationInput({
      amount:
        reservation.compensation?.amount !== undefined && reservation.compensation?.amount !== null
          ? String(reservation.compensation.amount)
          : '',
      currency: reservation.compensation?.currency || 'egp'
    });
    setInsuranceInput({
      amount:
        reservation.insuranceCollected?.amount !== undefined &&
        reservation.insuranceCollected?.amount !== null
          ? String(reservation.insuranceCollected.amount)
          : '',
      currency: reservation.insuranceCollected?.currency || 'egp'
    });
  }, [reservation]);

  const handleSave = () => {
    if (!reservation) return;
    setIsSavingFinancial(true);
    const refundAmount = refundInput?.amount ? parseFloat(refundInput.amount) : null;
    const compensationAmount = compensationInput?.amount
      ? parseFloat(compensationInput.amount)
      : null;
    const insuranceAmount = insuranceInput?.amount ? parseFloat(insuranceInput.amount) : null;

    const updateData: any = { id: reservation.id };

    if (refundAmount !== null && !isNaN(refundAmount)) {
      updateData.refund = { amount: refundAmount, currency: refundInput.currency };
    } else {
      updateData.refund = null;
    }

    if (compensationAmount !== null && !isNaN(compensationAmount)) {
      updateData.compensation = {
        amount: compensationAmount,
        currency: compensationInput.currency
      };
    } else {
      updateData.compensation = null;
    }

    if (insuranceAmount !== null && !isNaN(insuranceAmount)) {
      updateData.insuranceCollected = {
        amount: insuranceAmount,
        currency: insuranceInput.currency
      };
    } else {
      updateData.insuranceCollected = null;
    }

    const promise = updateOneReservations(updateData).then(() => setIsSavingFinancial(false));

    birdToast.promise(promise, {
      loadingMessage: 'Saving changes...',
      successMessage: 'Financial adjustments saved successfully.',
      errorFailedMessage: 'Failed to save financial adjustments.'
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {/* Refund Field */}
        <div className="flex flex-col gap-2 p-3 rounded-lg border border-gray-100 bg-gray-50/50">
          <span className="text-base font-gilroy-bold font-bold uppercase tracking-wide text-neutral-500">
            Refund
          </span>
          <div className="flex gap-2 items-center">
            <div className="flex-1">
              <InputText
                data={{ value: refundInput.amount }}
                config={{ type: InputTextType.number, disabled: !canEdit }}
                eventHandlers={{
                  onChangeHandler: (val: string) =>
                    setRefundInput((prev) => ({ ...prev, amount: val }))
                }}
              />
            </div>
            <div className="w-32">
              <Select
                value={{ value: refundInput.currency, label: refundInput.currency.toUpperCase() }}
                onChange={(selected: any) =>
                  setRefundInput((prev) => ({ ...prev, currency: selected?.value || 'egp' }))
                }
                options={CURRENCY_OPTIONS}
                isDisabled={!canEdit}
                styles={{
                  control: (base: any) => ({
                    ...base,
                    minHeight: '44px',
                    height: '44px'
                  })
                }}
              />
            </div>
          </div>
        </div>

        {/* Compensation Field */}
        <div className="flex flex-col gap-2 p-3 rounded-lg border border-gray-100 bg-gray-50/50">
          <span className="text-base font-gilroy-bold font-bold uppercase tracking-wide text-neutral-500">
            Compensation
          </span>
          <div className="flex gap-2 items-center">
            <div className="flex-1">
              <InputText
                data={{ value: compensationInput.amount }}
                config={{ type: InputTextType.number, disabled: !canEdit }}
                eventHandlers={{
                  onChangeHandler: (val: string) =>
                    setCompensationInput((prev) => ({ ...prev, amount: val }))
                }}
              />
            </div>
            <div className="w-32">
              <Select
                value={{
                  value: compensationInput.currency,
                  label: compensationInput.currency.toUpperCase()
                }}
                onChange={(selected: any) =>
                  setCompensationInput((prev) => ({
                    ...prev,
                    currency: selected?.value || 'egp'
                  }))
                }
                options={CURRENCY_OPTIONS}
                isDisabled={!canEdit}
                styles={{
                  control: (base: any) => ({
                    ...base,
                    minHeight: '38px',
                    height: '38px'
                  })
                }}
              />
            </div>
          </div>
        </div>

        {/* Insurance Collected Field */}
        <div className="flex flex-col gap-2 p-3 rounded-lg border border-gray-100 bg-gray-50/50">
          <span className="text-base font-gilroy-bold font-bold uppercase tracking-wide text-neutral-500">
            Insurance Collected
          </span>
          <div className="flex gap-2 items-center">
            <div className="flex-1">
              <InputText
                data={{ value: insuranceInput.amount }}
                config={{ type: InputTextType.number, disabled: !canEdit }}
                eventHandlers={{
                  onChangeHandler: (val: string) =>
                    setInsuranceInput((prev) => ({ ...prev, amount: val }))
                }}
              />
            </div>
            <div className="w-32">
              <Select
                value={{
                  value: insuranceInput.currency,
                  label: insuranceInput.currency.toUpperCase()
                }}
                onChange={(selected: any) =>
                  setInsuranceInput((prev) => ({
                    ...prev,
                    currency: selected?.value || 'egp'
                  }))
                }
                options={CURRENCY_OPTIONS}
                isDisabled={!canEdit}
                styles={{
                  control: (base: any) => ({
                    ...base,
                    minHeight: '38px',
                    height: '38px'
                  })
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {canEdit && (
        <div className="mt-4 flex justify-end">
          <Button
            onClick={handleSave}
            size={ButtonSize.medium}
            disabled={isSavingFinancial}
            loading={isSavingFinancial}
          >
            Save Changes
          </Button>
        </div>
      )}
    </div>
  );
};
